/*  PERCUP — PER-312 · Back-in-stock from COLLECTION cards
 *
 *  Goal: sold-out cards open KLAVIYO'S OWN back-in-stock popup (the identical
 *  form shoppers see on the PDP), on the same page, and submitting it must
 *  create the same subscription the PDP would.
 *
 *  Why this shape
 *  --------------
 *  1. Klaviyo's app embed only injects its BIS *button* on product pages, and
 *     the legacy `klaviyo.enable('backinstock')` API is a no-op in their current
 *     onsite build — verified: init/enable are generic queue-pushers and the
 *     per-card containers were never populated.
 *  2. `klaviyo.openForm(FORM_ID)` DOES render the real BIS popup anywhere,
 *     including collection pages. That gives us Klaviyo's exact UI.
 *  3. But that popup carries no product context, and Klaviyo's docs are explicit
 *     that you cannot pass a variant to openForm. Confirmed empirically: when the
 *     form is opened this way, submitting it fires NO back-in-stock request.
 *
 *  So we let Klaviyo render + validate the form, then perform the subscription
 *  ourselves against Klaviyo's client API using the variant from the card that
 *  was clicked — byte-identical to the request the PDP popup makes:
 *
 *    POST https://a.klaviyo.com/client/back-in-stock-subscriptions/
 *         ?company_id=<key>&revamp=true
 *    { data: { type: 'back-in-stock-subscription',
 *              attributes: { channels: ['EMAIL'], profile: {...} },
 *              relationships: { variant: { data: {
 *                type: 'catalog-variant',
 *                id: '$shopify:::$default:::<variantId>' } } } } }
 *
 *  Net effect: same popup, same page, same subscription, same flow email.
 */
(function () {
  'use strict';

  var COMPANY_ID = window.PCP_KLAVIYO_COMPANY_ID || '';
  var FORM_ID = window.PCP_KLAVIYO_BIS_FORM_ID || '';
  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  // Klaviyo API version. Required — omitting it yields 400. Bump if Klaviyo
  // deprecates this revision.
  var KLAVIYO_REVISION = window.PCP_KLAVIYO_REVISION || '2025-04-15';

  // Variant of the card whose bell was clicked — the context Klaviyo lacks.
  var pendingVariantId = null;
  var lastSubmitted = null; // guards against double-fires from Klaviyo's re-renders

  function bisForm() {
    return FORM_ID
      ? document.querySelector('[data-testid="klaviyo-form-' + FORM_ID + '"]')
      : null;
  }

  function subscribe(email, variantId) {
    if (!COMPANY_ID || !email || !variantId) return;

    var key = email + '|' + variantId;
    if (lastSubmitted === key) return;
    lastSubmitted = key;

    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    };

    // Headers matter: without `revision` Klaviyo returns 400. These mirror the
    // request Klaviyo's own PDP popup sends (verified against a 202 response).
    fetch(ENDPOINT + '?company_id=' + encodeURIComponent(COMPANY_ID) + '&revamp=true', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
        'x-klaviyo-js-url': 'query',
        'x-klaviyo-onsite': '1'
      },
      body: JSON.stringify(payload),
      keepalive: true // survive if Klaviyo's UI navigates/re-renders straight after
    }).catch(function () { /* non-fatal: Klaviyo still shows its own success state */ });
  }

  function emailFromForm(form) {
    var input = form && form.querySelector('input[type="email"], input[name*="email" i]');
    return input ? (input.value || '').trim() : '';
  }

  // Klaviyo re-renders its form constantly, so bind at document level in the
  // CAPTURE phase — we read the email before their handler clears/replaces it.
  function handleSubmitIntent(e) {
    if (!pendingVariantId) return;
    var form = bisForm();
    if (!form) return;
    if (!form.contains(e.target)) return;

    // Only react to an actual submit attempt, not stray clicks in the popup.
    var isSubmit =
      e.type === 'submit' ||
      (e.target.closest && e.target.closest('button, [role="button"]'));
    if (!isSubmit) return;

    var email = emailFromForm(form);
    if (!email || email.indexOf('@') < 1) return; // let Klaviyo show its own validation
    subscribe(email, pendingVariantId);
  }

  document.addEventListener('submit', handleSubmitIntent, true);
  document.addEventListener('click', handleSubmitIntent, true);
  // Enter-to-submit inside the email field.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') handleSubmitIntent(e);
  }, true);

  // ── Card triggers ────────────────────────────────────────────────────────
  // Delegated so it survives facet/pagination AJAX re-renders of the grid.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-bis-trigger]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    pendingVariantId = trigger.getAttribute('data-variant-id') || null;
    lastSubmitted = null;

    if (window.klaviyo && typeof window.klaviyo.openForm === 'function' && FORM_ID) {
      window.klaviyo.openForm(FORM_ID);
      return;
    }

    // Klaviyo unavailable (blocked/not loaded) — the PDP always has a working
    // BIS button, so send the shopper there rather than leaving a dead control.
    var url = trigger.getAttribute('data-product-url-clean') ||
              trigger.getAttribute('data-product-url');
    if (url) window.location.href = url;
  });
})();
